import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { ApplicationConfig, inject, provideAppInitializer, provideZonelessChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideBxNotification, provideDaterangepickerLocale, provideTooltip } from '@beyonnex/bx-angular-ui';
import { provideNuMonacoEditorConfig } from '@ng-util/monaco-editor';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { ModuleRegistry } from 'ag-grid-community';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';
import { AbstractSecurityStorage, provideAuth } from 'angular-auth-oidc-client';
import { AG_GRID_LICENSE } from '../../ag-grid-license';
import { routes } from './app.routes';
import { authConfig } from './auth/auth.config';
import { authInterceptor } from './interceptors/auth-interceptor';
import { traceparentInterceptor } from './interceptors/traceparent-interceptor';
import { AuthStorageService } from './services/auth/auth-local-storage.service';
import { LocalizationService } from './services/localization/localization.service';

configureAgGrid();

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(routes),
    provideDaterangepickerLocale({
      separator: ' - ',
      applyLabel: 'Ok',
      displayFormat: 'DD.MM.YYYY',
    }),
    provideAuth(authConfig),
    { provide: AbstractSecurityStorage, useClass: AuthStorageService },
    // withXhr() keeps the XHR backend: the fetch backend can't report upload progress, which the ELS import flows need.
    // The traceparent interceptor comes first so it is outermost: it is the last to see a failure and stamps it with the trace.
    provideHttpClient(withXhr(), withInterceptors([traceparentInterceptor, authInterceptor])),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/i18n/',
        suffix: '.json',
      }),
    }),
    provideNuMonacoEditorConfig({
      baseUrl: `https://cdnjs.cloudflare.com/ajax/libs/monaco-editor/0.52.2/min`, // version 0.53.0 had rendering issues
    }),
    provideAppInitializer(() => inject(LocalizationService).init()),
    provideTooltip(),
    // Translations are not loaded when this config is read, so NotificationsService overrides the label per call.
    provideBxNotification({ closeAriaLabel: 'Close', position: 'top-right' }),
  ],
};

function configureAgGrid(): void {
  ModuleRegistry.registerModules([AllEnterpriseModule]);
  LicenseManager.setLicenseKey(AG_GRID_LICENSE);
}
